import React from "react";
import { motion } from "framer-motion";
import { ABOUT } from "@/constants/testIds";
import { useSite } from "@/lib/site";

const ABOUT_IMG =
    "https://images.unsplash.com/photo-1579724175242-0204ecac28cb?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1600";

export default function About() {
    const site = useSite();
    return (
        <section
            id="about"
            data-testid={ABOUT.section}
            className="py-24 md:py-40 border-t border-[#E6E2D6]"
        >
            <div className="mx-auto max-w-[1800px] px-6 md:px-10 lg:px-16">
                <div className="grid grid-cols-12 gap-6 md:gap-8 mb-16 md:mb-24">
                    <div className="col-span-12 md:col-span-3">
                        <p className="eyebrow text-[#1B1B1B]/70">
                            § About
                        </p>
                    </div>
                    <div className="col-span-12 md:col-span-9">
                        <motion.h2
                            data-testid={ABOUT.heading}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-15%" }}
                            transition={{
                                duration: 1,
                                ease: [0.16, 1, 0.3, 1],
                            }}
                            className="font-serif-editorial text-5xl md:text-7xl lg:text-[104px] leading-[0.98] tracking-[-0.02em] text-[#1B1B1B] max-w-5xl"
                        >
                            An independent{" "}
                            <em className="italic text-[#A8844F]">practice,</em>{" "}
                            founded to <br className="hidden md:block" />
                            work slowly and stay small.
                        </motion.h2>
                    </div>
                </div>

                <div className="grid grid-cols-12 gap-6 md:gap-8">
                    {/* Portrait / architectural aside */}
                    <div className="col-span-12 md:col-span-5">
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-10%" }}
                            transition={{
                                duration: 1.1,
                                ease: [0.16, 1, 0.3, 1],
                            }}
                            className="img-frame aspect-[4/5] bg-[#1B1B1B]"
                        >
                            <img
                                src={ABOUT_IMG}
                                alt="Concrete bench with geometric shadows"
                                loading="lazy"
                                className="w-full h-full object-cover"
                            />
                        </motion.div>
                        <p className="mt-4 font-sans-tight text-[11px] tracking-[0.2em] uppercase text-[#1B1B1B]/60">
                            Fig. 02 · Study of light, restraint, structure
                        </p>
                    </div>

                    {/* Editorial paragraph */}
                    <div className="col-span-12 md:col-span-6 md:col-start-7">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-10%" }}
                            transition={{
                                duration: 1,
                                delay: 0.2,
                                ease: [0.16, 1, 0.3, 1],
                            }}
                            className="font-sans-tight text-lg md:text-xl leading-[1.6] text-[#1B1B1B] font-light space-y-8"
                        >
                            {(site.about_paragraphs || []).map((p, i) => (
                                <p key={i}>
                                    {i === 0 && (
                                        <span className="font-serif-editorial text-[#A8844F] text-2xl italic mr-2">
                                            ¶
                                        </span>
                                    )}
                                    {p}
                                </p>
                            ))}

                            <div className="pt-6 border-t border-[#E6E2D6] flex flex-col md:flex-row gap-6 md:gap-14 mt-8">
                                <div>
                                    <p className="eyebrow text-[#1B1B1B]/60 mb-2">
                                        Founded
                                    </p>
                                    <p className="font-serif-editorial text-3xl text-[#1B1B1B]">
                                        2026
                                    </p>
                                </div>
                                <div>
                                    <p className="eyebrow text-[#1B1B1B]/60 mb-2">
                                        Discipline
                                    </p>
                                    <p className="font-serif-editorial text-3xl text-[#1B1B1B]">
                                        Design, in full
                                    </p>
                                </div>
                                <div>
                                    <p className="eyebrow text-[#1B1B1B]/60 mb-2">
                                        Availability
                                    </p>
                                    <p className="font-serif-editorial text-3xl text-[#1B1B1B]">
                                        Selective
                                    </p>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </div>
        </section>
    );
}
